import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ForClinics from "@/pages/ForClinics";
import Materials from "@/pages/Materials";
import Workflow from "@/pages/Workflow";
import About from "@/pages/About";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import Contact from "@/pages/Contact";
import ResetPassword from "@/pages/ResetPassword";
import SetPassword from "@/pages/SetPassword";
import Portal from "@/pages/Portal";
import Admin from "@/pages/Admin";
import RND from "@/pages/RND";
import Verify from "@/pages/Verify";
import Track from "@/pages/Track";
import Feedback from "@/pages/Feedback";
import Reviews from "@/pages/Reviews";
import Compliance from "@/pages/Compliance";
import CaseDetail from "@/pages/CaseDetail";
import Pricing from "@/pages/Pricing";
import Toothmatrix from "@/pages/Toothmatrix";
import Signup from "@/pages/Signup";
import PrepGuidelines from "@/pages/PrepGuidelines";
import Trust from "@/pages/Trust";
import ShareholderDashboard from "@/pages/ShareholderDashboard";
import DentalPractice from "@/pages/solutions/DentalPractice";
import DentalLabs from "@/pages/solutions/DentalLabs";
import PricingLabs from "@/pages/PricingLabs";
import WarrantyPassport from "@/pages/WarrantyPassport";
import ClaimTracker from "@/pages/ClaimTracker";
import CaseStudyPatientX from "@/pages/CaseStudyPatientX";
import CaseStudies from "@/pages/CaseStudies";
import PracticeRegister from "@/pages/PracticeRegister";
import PracticePortal from "@/pages/PracticePortal";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n/I18nContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { TenantBrandProvider } from "@/contexts/TenantBrandContext";
import { ThemeProvider } from "@/lib/theme";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installGlobalErrorHandlers } from "@/lib/errorReporter";

function App() {
    useEffect(() => { installGlobalErrorHandlers(); }, []);
    return (
        <div className="App">
            <ErrorBoundary>
                <ThemeProvider>
                    <I18nProvider>
                        <AuthProvider>
                            <TenantBrandProvider>
                                <BrowserRouter>
                                    <Layout>
                                    <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/for-clinics" element={<ForClinics />} />
                                        {/* Legacy URLs preserved for SEO + old bookmarks */}
                                        <Route path="/products" element={<Navigate to="/for-clinics#restoratives" replace />} />
                                        <Route path="/catalog" element={<Navigate to="/for-clinics" replace />} />
                                        <Route path="/rnd" element={<RND />} />
                                        <Route path="/technology" element={<Materials />} />
                                        <Route path="/workflow" element={<Workflow />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/signup" element={<Signup />} />
                                        <Route path="/start" element={<Navigate to="/signup" replace />} />
                                        <Route path="/auth/callback" element={<AuthCallback />} />
                                        <Route path="/forgot-password" element={<ForgotPassword />} />
                                        <Route path="/reset-password" element={<ResetPassword />} />
                                        {/* iter 82c — single-use admin-issued "set your password" link */}
                                        <Route path="/set-password" element={<SetPassword />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/portal" element={<Portal />} />
                                        <Route path="/admin" element={<Admin />} />
                                        <Route path="/shareholder/dashboard" element={<ShareholderDashboard />} />
                                        <Route path="/shareholder" element={<Navigate to="/shareholder/dashboard" replace />} />
                                        <Route path="/verify/:caseNumber" element={<Verify />} />
                                        <Route path="/track/:caseNumber" element={<Track />} />
                                        <Route path="/feedback" element={<Feedback />} />
                                        <Route path="/feedback/:caseNumber" element={<Feedback />} />
                                        <Route path="/reviews" element={<Reviews />} />
                                        {/* Public Trust Center — surfaced as "Compliance" in the top nav. */}
                                        <Route path="/compliance" element={<Compliance />} />
                                        <Route path="/cases/:caseId" element={<CaseDetail />} />
                                        <Route path="/pricing" element={<Pricing />} />
                                        <Route path="/credits" element={<Pricing />} />
                                        <Route path="/toothmatrix" element={<Toothmatrix />} />
                                        <Route path="/solutions/dental-practice" element={<DentalPractice />} />
                                        <Route path="/solutions/dental-labs" element={<DentalLabs />} />
                                        <Route path="/pricing/labs" element={<PricingLabs />} />
                                        <Route path="/warranty/:caseNumber" element={<WarrantyPassport />} />
                                        <Route path="/claim/:claimNumber" element={<ClaimTracker />} />
                                        <Route path="/case-studies" element={<CaseStudies />} />
                                        <Route path="/case-studies/patient-x" element={<CaseStudyPatientX />} />
                                        <Route path="/practice/register" element={<PracticeRegister />} />
                                        <Route path="/practice/portal" element={<PracticePortal />} />
                                        <Route path="/prep-guidelines" element={<PrepGuidelines />} />
                                        <Route path="/preparation-guidelines" element={<Navigate to="/prep-guidelines" replace />} />
                                        <Route path="/dental-automaton" element={<Toothmatrix />} />
                                        <Route path="/trust" element={<Trust />} />
                                        <Route path="/security" element={<Navigate to="/trust" replace />} />
                                    </Routes>
                                </Layout>
                            </BrowserRouter>
                            </TenantBrandProvider>
                        </AuthProvider>
                    </I18nProvider>
                </ThemeProvider>
            </ErrorBoundary>
            <Toaster position="bottom-right" richColors />
        </div>
    );
}

export default App;
